import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Sparkles, Search, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function FindDomainCard() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(null); // "real" | "heuristic"

  const search = async (e) => {
    e?.preventDefault?.();
    if (query.trim().length < 2) {
      toast.error("Enter at least 2 characters");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/domain-search`, { params: { query: query.trim() } });
      setResults(data.results || []);
      setMode(data.using_real_api ? "rdap" : "heuristic");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mb-10" data-testid="find-domain-card">
      <div className="rounded-3xl bg-gradient-to-br from-[#FEE7E1] via-white to-white border border-[#F26953]/20 overflow-hidden">
        <div className="p-5 sm:p-8 md:p-10">
          <div className="flex items-start gap-3 sm:gap-4 mb-6">
            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-2xl bg-[#F26953] grid place-items-center flex-shrink-0 shadow-sm">
              <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-[#F26953] mb-1">
                No domain yet? Start here.
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0F172A] tracking-tight">
                Find me a domain name.
              </h2>
              <p className="text-sm text-[#475569] mt-2 max-w-xl">
                Type your business name and we'll suggest brand-ready domains across <span className="font-semibold text-[#0F172A]">.com</span>, <span className="font-semibold text-[#0F172A]">.co</span>, and <span className="font-semibold text-[#0F172A]">.io</span>. One click to grab it on Dynadot — your refcode auto-attaches.
              </p>
            </div>
          </div>

          <form onSubmit={search} className="flex flex-col sm:flex-row gap-3 max-w-2xl" data-testid="find-domain-form">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. 'Acme Cafe' or 'Thai restaurant'"
                data-testid="find-domain-input"
                className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-[#E2E8F0] bg-white focus:ring-2 focus:ring-[#F26953]/20 focus:border-[#F26953] outline-none text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="find-domain-submit"
              className="inline-flex items-center justify-center gap-2 bg-[#F26953] text-white font-semibold px-6 py-3.5 rounded-xl hover:bg-[#D85340] disabled:opacity-60 transition-colors shadow-sm"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {loading ? "Searching…" : "Find domains"}
            </button>
          </form>

          {results && (
            <div className="mt-6 space-y-2" data-testid="find-domain-results">
              {!mode || mode === "heuristic" ? (
                <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 inline-flex items-center gap-1.5 mb-2">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400"></span>
                  Curated suggestions — confirm availability on Dynadot.
                </div>
              ) : (
                <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 inline-flex items-center gap-1.5 mb-2">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                  Live availability checked via RDAP (the official domain registry protocol).
                </div>
              )}
              {results.length === 0 && (
                <div className="text-sm text-slate-500">No suggestions. Try a different keyword.</div>
              )}
              <ul className="divide-y divide-[#F1E5E1] rounded-2xl border border-[#F1E5E1] bg-white overflow-hidden">
                {results.map((r) => {
                  const avail = r.availability || (r.available ? "available" : "registered");
                  const isAvail = avail === "available";
                  const isUnknown = avail === "unknown";
                  const statusDot = isAvail ? "bg-emerald-500"
                    : isUnknown ? "bg-amber-500"
                    : "bg-slate-300";
                  const statusText = isAvail ? "Available" : isUnknown ? "Check availability" : "Registered";
                  const statusTextColor = isAvail ? "text-emerald-600"
                    : isUnknown ? "text-amber-600"
                    : "text-slate-400";
                  return (
                  <li
                    key={r.domain}
                    data-testid={`find-domain-result-${r.domain}`}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 py-3 sm:px-4 sm:py-4 gap-3 hover:bg-[#FFF8F6] transition-colors"
                  >
                    <div className="min-w-0 w-full sm:w-auto sm:flex-1">
                      {/* Domain name — full width on mobile, mono, sized for readability */}
                      <div
                        className="font-mono font-semibold text-[#0F172A] text-base sm:text-[15px] leading-snug"
                        style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}
                        data-testid={`find-domain-name-${r.domain}`}
                      >
                        {r.domain}
                      </div>
                      <div className={`mt-1 text-xs font-semibold inline-flex items-center gap-1.5 ${statusTextColor}`}>
                        <span className={`inline-block h-1.5 w-1.5 rounded-full ${statusDot}`}></span>
                        {statusText}
                      </div>
                    </div>
                    <a
                      href={r.register_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`find-domain-buy-${r.domain}`}
                      className={`w-full sm:w-auto inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-4 py-2.5 sm:py-2 rounded-lg transition-colors whitespace-nowrap flex-shrink-0 ${
                        isAvail
                          ? "bg-[#F26953] text-white hover:bg-[#D85340]"
                          : "bg-white border border-[#E2E8F0] text-[#475569] hover:bg-slate-50"
                      }`}
                    >
                      {isAvail ? "Get it" : "Check"}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </li>
                  );
                })}
              </ul>
              <p className="text-[11px] text-slate-400 mt-2">
                After you buy, come back here and paste it in "Add domain" — we'll wire up email in 2 clicks.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
